import { InvoiceTemplate, Sending } from 'src/modules/invoice/models/invoiceTemplate';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';

/**
 * Förlagor för nya fakturamallar, en per malltyp.
 *
 * Värdena är hämtade ur mallar som redan finns i drift, inte valda på fri hand. En swishmall har
 * hittills skapats genom att en autogirorad kopierats i databasen och fått type ändrad, och de fem
 * som finns är identiska. Förlagan nedan återger dem, så att en mall skapad härifrån ser likadan ut
 * som de körningarna redan fungerar med.
 *
 * yvMultiCust_id och yvMultiCustName sätts av databasen (suser_id och suser_name) när API:et infogar
 * raden som kundens användare, så de lämnas här.
 */

export type NewInvoiceTemplateType = 'member' | 'autogiro' | 'swish';

// Fälten som varje försändelse behöver ha satta. Resten fylls av API:et vid skapandet.
type SendingBlueprint = Pick<
  Sending,
  | 'sendingType'
  | 'isReminder'
  | 'method'
  | 'status'
  | 'notReady'
  | 'textApproved'
  | 'customizeText'
  | 'customerEditEnabled'
  | 'expireDate'
  | 'reminderDate'
  | 'articleGroup'
  | 'defaultHeading'
  | 'defaultText'
  | 'emailHeading'
  | 'emailText'
>;

const emptySending = (sendingType: number): SendingBlueprint => ({
  sendingType,
  isReminder: false,
  method: '',
  status: 0,
  // En ny mall har inga texter än, så försändelserna är inte klara. Märkningen försvinner när
  // texten sparas, samma väg som för en mall vi satt upp för hand.
  notReady: true,
  textApproved: false,
  customizeText: false,
  customerEditEnabled: true,
  expireDate: '',
  reminderDate: '',
  articleGroup: '',
  defaultHeading: '',
  defaultText: '',
  emailHeading: '',
  emailText: '',
});

// Gemensamt för alla typer: tomma strängar i stället för null, som i mallarna vi satt upp själva.
const baseTemplate = (ownerId: number, name: string, type: NewInvoiceTemplateType): Partial<InvoiceTemplate> => ({
  owner_id: ownerId,
  name: name.trim(),
  type,
  status: '0',
  // Utan den här filtreras mallen bort ur fakturamallslistan och kunden hittar den aldrig.
  showFormInfo: true,
  selection: '',
  selection_id: 0,
  profilesInclude: '',
  profilesExclude: '',
  profilesMark: '',
  expireDateFrom: '',
  expireDateTom: '',
  payedDateFrom: '',
  payedDateTom: '',
  resdate: '',
  email: '',
  preReceiverText: '',
  cardInfo: '',
  newspaper: 0,
  memberType: 0,
  recruit: 0,
  payIntervalFrom: 0,
  payIntervalTom: 0,
  agPeriodicities: 0,
  printZeroInvoice: false,
  approvePaymentsGreaterThanInvoiced: false,
});

export interface InvoiceTemplateBlueprint {
  type: NewInvoiceTemplateType;
  // Autogiro och swish hör ihop med autojobb och avtal hos Bankgirot respektive Swish, och sätts
  // upp av oss. Kunden skapar bara vanliga medlemsmallar.
  superAdminOnly?: boolean;
  // Nycklar i invoice-namnrymden.
  labelKey: string;
  descriptionKey: string;
  build: (ownerId: number, name: string) => Partial<InvoiceTemplate>;
}

export const invoiceTemplateBlueprints: InvoiceTemplateBlueprint[] = [
  {
    type: 'member',
    labelKey: 'invoice:invoiceMissionTypeText.member',
    descriptionKey: 'invoice:adminInvoiceTemplatePage.createTemplate.memberDescription',
    build: (ownerId, name) => ({
      ...baseTemplate(ownerId, name, 'member'),
      formTemplate: 'bgocr_2024',
      householdInvoice: true,
      suppressAcknowledgement: false,
      sendings: [
        // E-postförsändelsen skickar mejlet, huvudutskicket fakturan, sedan två påminnelser.
        { ...emptySending(InvoiceSendingTypeIntegerEnum.Email), method: 'mail' },
        { ...emptySending(InvoiceSendingTypeIntegerEnum.Main), method: 'letter', expireDate: '30' },
        emptySending(InvoiceSendingTypeIntegerEnum.Reminder1),
        emptySending(InvoiceSendingTypeIntegerEnum.Reminder2),
      ] as Sending[],
    }),
  },
  {
    type: 'autogiro',
    superAdminOnly: true,
    labelKey: 'invoice:invoiceMissionTypeText.autogiro',
    descriptionKey: 'invoice:adminInvoiceTemplatePage.createTemplate.autogiroDescription',
    build: (ownerId, name) => ({
      ...baseTemplate(ownerId, name, 'autogiro'),
      // Autogiroavin, samma blankett som mallarna i drift använder.
      formTemplate: 'agavi',
      householdInvoice: true,
      suppressAcknowledgement: false,
      sendings: [
        { ...emptySending(InvoiceSendingTypeIntegerEnum.Main), method: 'letter', expireDate: '30' },
        // Andra raden är avisering om misslyckad dragning, inte en vanlig påminnelse.
        { ...emptySending(InvoiceSendingTypeIntegerEnum.Reminder1), method: 'letter', expireDate: '30' },
      ] as Sending[],
    }),
  },
  {
    type: 'swish',
    superAdminOnly: true,
    labelKey: 'invoice:invoiceMissionTypeText.swish',
    descriptionKey: 'invoice:adminInvoiceTemplatePage.createTemplate.swishDescription',
    build: (ownerId, name) => ({
      ...baseTemplate(ownerId, name, 'swish'),
      // Swish skickar ingen avi, men mallarna i drift har agavi kvar sedan de kopierades från
      // autogiro. Nya mallar sätts likadant så raderna ser ut som de som redan fungerar.
      formTemplate: 'agavi',
      // Swishmallarna i drift har ingen samlingsfaktura per hushåll, till skillnad från autogiro.
      householdInvoice: false,
      // Dragningen sker automatiskt, så den behöver inget godkännandemail.
      suppressAcknowledgement: true,
      sendings: [
        {
          ...emptySending(InvoiceSendingTypeIntegerEnum.Main),
          method: 'letter',
          expireDate: '1',
          articleGroup: 'AG011',
          textApproved: true,
          notReady: false,
        },
        emptySending(InvoiceSendingTypeIntegerEnum.Reminder1),
      ] as Sending[],
    }),
  },
];

export const findInvoiceTemplateBlueprint = (type: NewInvoiceTemplateType) =>
  invoiceTemplateBlueprints.find((b) => b.type === type) ?? invoiceTemplateBlueprints[0];
